import { batch } from 'react-redux';
import { Pdp } from '@ff-os/pdp-shared-registry/Pdp';
import { toList } from '@ff-os/shared-utils/dictionaries';
import { upgrade } from '@ff-os/shared-utils/upgrades/upgrades';
import { selectActiveVariantId, selectAllVariants } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { actions as inputActions } from '@ff-scene-plugins/gift-redux/slices/inputsSlice';
import { actions as metaActions } from '@ff-scene-plugins/gift-redux/slices/metaSlice';
import { actions as variableActions } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { actions as versionActions } from '@ff-scene-plugins/gift-redux/slices/versionSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { matchActiveVariant } from '@ff-scene-plugins/gift-shared/matchActiveVariant';
import { giftUpgrades } from '@ff-scene-plugins/gift-shared/upgrades/giftUpgrades';
import { selectActivePrefabId, updateActiveVariantId } from '../_redux-meta';
import { applyPrefab, selectPrefabs } from '../_redux-prefabs';
import { selectContentFirst } from '../_redux-scene';
import { applyDefaultPrefabForActiveVariant } from '../helpers/applyDefaultPrefabForActiveVariant';
import { migratePinnedVariablesAndInputs } from './migratePinnedVariablesAndInputs';

export const openProject = async (
    loadedProject: GiftJson.Project,
    isCompatible: boolean,
    source: Pdp.Scene.ProjectSource,
    dispatch: GiftPdpDispatch,
    getState: () => GiftPdpState
) => {

    loadedProject = upgrade(loadedProject, { allUpgrades: giftUpgrades });

    const state = getState();
    const isContentFirst = selectContentFirst(state);

    // In content-first mode, preserve the current prefab (set by __idt_show via applyPrefab)
    // instead of loading the saved project's prefab from the draft.
    const currentPrefabId = selectActivePrefabId(state);

    const startupProject = state.project as GiftJson.Project;
    const migratedProject = migrate(loadedProject, startupProject, isCompatible, isContentFirst);

    batch(() => {

        dispatch(inputActions.upsertMany(toList(migratedProject.inputs.entities)));
        dispatch(variableActions.setAll(toList(migratedProject.variables.entities)));
        dispatch(metaActions.overwrite(migratedProject.meta));
        dispatch(versionActions.overwrite(migratedProject.version));

        // In content-first mode, always re-apply the current prefab after loading the project
        if (isContentFirst && currentPrefabId) {
            const prefabs = selectPrefabs(getState());
            const prefabToApply = prefabs[currentPrefabId];
            if (prefabToApply) {
                dispatch(applyPrefab(prefabToApply));
            }
        } else {
            const variants = selectAllVariants(getState());
            if (hasVariantChanged(migratedProject, variants)) {
                dispatch(updateActiveVariantId(selectActiveVariantId(getState())));
                applyDefaultPrefabForActiveVariant(dispatch, getState);
            }
        }
    });

    return { continue: true };
};

const migrate = (
    loadedProject: GiftJson.Project,
    startupProject: GiftJson.Project,
    isCompatible: boolean,
    isContentFirst: boolean
) => {
    let migratedProject = loadedProject;

    if (!isCompatible) {
        migratedProject = migratePinnedVariablesAndInputs(loadedProject, startupProject);
    }

    // In content-first mode, preserve variable options from startup project (prefab settings)
    // This ensures properties like 'visible: false' are not lost when loading drafts
    if (isContentFirst) {
        migratedProject = mergeVariableOptions(migratedProject, startupProject);
    }

    return migratedProject;
};

const mergeVariableOptions = (
    loadedProject: GiftJson.Project,
    startupProject: GiftJson.Project
): GiftJson.Project => {
    const mergedVariables: any = {};

    for (const id of loadedProject.variables.ids) {
        const loadedVar = loadedProject.variables.entities[id]!;
        const startupVar = startupProject.variables.entities[id];

        if (startupVar) {
            // Merge: keep position/values from loaded, but options from startup (prefab)
            mergedVariables[id] = {
                ...loadedVar,
                options: {
                    ...loadedVar.options,
                    ...startupVar.options  // Startup options take precedence
                }
            };
        } else {
            // Variable doesn't exist in startup, keep as-is
            mergedVariables[id] = loadedVar;
        }
    }

    return {
        ...loadedProject,
        variables: {
            ...loadedProject.variables,
            entities: mergedVariables
        }
    };
};

const hasVariantChanged = (
    migratedProject: GiftJson.Project,
    variants: GiftJson.Variant<any>[]
) => {
    const activeVariantAfterMigration = matchActiveVariant(variants, migratedProject.inputs.entities);
    return activeVariantAfterMigration.id !== migratedProject.meta.activeVariantId;
};